import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import PlacesAutocomplete from 'react-places-autocomplete';
import PlaceIcon from '@mui/icons-material/Place';
import { inputsStyle } from './styles';
import { Colors, CountryList, useGlobalStyles } from 'utils';
import { ValidationInput } from './validationInput';
import { TextField } from '@mui/material';
import { ErrMessage } from '../messages';
import { InputMinLoader } from './inputMiniLoader';
import { SelectTypeAutocomplete } from './selectTypeAutocomplete';
import { AddressService, httpRequestsOnLoadActions } from 'store';

const defaultCountry = 'United States';
const ADDRESS_LOAD = 'ADDRESS_LOAD';
/** Fields that change where an address is (unlike the unit number) — editing one drops its coordinates. */
const ADDRESS_LOCATION_FIELDS = ['street', 'city', 'state', 'zip', 'country'];
/** Pause after the last keystroke before a hand-typed address is geocoded. */
const ADDRESS_GEOCODE_DEBOUNCE_MS = 800;
export const AddressInput = ({
    selectedAddress,
    setCurrentAddress,
    oneInput,
    placeholder,
    disabled,
    errMessage,
    noBlue,
    noSuite,
    column,
    notRequired,
    noError,
    noAddressSuggest,
}) => {
    const classes = inputsStyle();
    const globalStyles = useGlobalStyles();
    const dispatch = useDispatch();
    const [googleReady, setGoogleReady] = useState(false);

    useEffect(() => {
        if (window?.google && window?.google.maps && window?.google?.maps?.places) {
            setGoogleReady(true);
        } else {
            const interval = setInterval(() => {
                if (window?.google?.maps?.places) {
                    setGoogleReady(true);
                    clearInterval(interval);
                }
            }, 100);

            return () => clearInterval(interval);
        }
    }, []);

    const [loading, setLoading] = useState(false);
    const [inputs, setInputs] = useState({
        city: '',
        country: defaultCountry,
        formattedAddress: '',
        lat: '',
        lng: '',
        state: '',
        street: '',
        zip: '',
    });

    const latestRef = useRef(null);
    const geocodeTimerRef = useRef(null);
    const geocodeSeqRef = useRef(0);
    useEffect(() => () => clearTimeout(geocodeTimerRef.current), []);

    const handleSelect = async (value, ev) => {
        // A suggestion pick geocodes itself — drop any pending hand-typed lookup.
        clearTimeout(geocodeTimerRef.current);
        geocodeSeqRef.current += 1;
        dispatch(httpRequestsOnLoadActions.appendLoading(ADDRESS_LOAD));
        setLoading(true);
        await AddressService.getAddress(value)
            .then((response) => {
                const params = {
                    ...inputs,
                };
                params['formattedAddress'] = response.data.formattedAddress;
                params['lat'] = response.data.lat;
                params['lng'] = response.data.lng;
                params['city'] = response.data.city;
                params['country'] = response.data.country;
                params['state'] = response.data.state;
                params['street'] = response.data.street;
                params['zip'] = response.data.zip;
                if (response.data.unitNumber) params['unitNumber'] = response.data.unitNumber;
                latestRef.current = params;
                setCurrentAddress(params);
                setInputs(params);
            })
            .catch((err) => {})
            .finally(() => {
                setLoading(false);
                dispatch(httpRequestsOnLoadActions.removeLoading(ADDRESS_LOAD));
            });
    };

    useEffect(() => {
        if (selectedAddress) {
            latestRef.current = selectedAddress;
            setInputs(selectedAddress);
        }
    }, [selectedAddress]);

    const handleChange = (e, name) => {
        const currentName = e?.target?.name ? e.target.name : name;
        const value = name === 'street' ? e : e?.target?.value;
        const params = {
            ...inputs,
        };
        params[currentName] = value;
        // A hand edit to where the address is invalidates the last geocode — keeping its lat/lng would
        // pin the new text at the old place (only a Google pick re-geocodes, see handleSelect). Clear
        // them, then re-geocode the typed text once typing pauses. The unit number doesn't move the point.
        const movesPoint = ADDRESS_LOCATION_FIELDS.includes(currentName) && value !== inputs?.[currentName];
        if (movesPoint) {
            params.lat = '';
            params.lng = '';
        }
        setInputs(params);

        let formattedAddress = '';
        if (params?.street) formattedAddress += `${params.street}, `;
        if (params?.city) formattedAddress += ` ${params.city}, `;
        if (params?.state) formattedAddress += ` ${params.state}, `;
        if (params?.zip) formattedAddress += ` ${params.zip}, `;
        if (params?.country) formattedAddress += ` ${params.country}`;
        params['formattedAddress'] = formattedAddress;
        latestRef.current = params;
        setCurrentAddress(params);
        if (movesPoint) scheduleGeocode(params);
    };

    /**
     * Geocode hand-typed text through the same `AddressService.getAddress` a suggestion pick uses,
     * taking ONLY lat/lng — the user's typed fields stay as entered. Debounced; a newer edit or a
     * suggestion pick supersedes a pending/in-flight lookup (sequence check), so a slow response
     * can't land late. The result merges into this form's own state, which is why it is a service
     * call from here rather than a store round-trip.
     */
    const scheduleGeocode = (params) => {
        clearTimeout(geocodeTimerRef.current);
        const seq = ++geocodeSeqRef.current;
        // Too little to place reliably — leave it without coordinates (it just won't map).
        if (!params?.street || !(params?.city || params?.zip)) return;
        const text = [params.street, params.city, params.state, params.zip, params.country].filter(Boolean).join(', ');
        geocodeTimerRef.current = setTimeout(() => {
            AddressService.getAddress(text)
                .then((response) => {
                    const { lat, lng } = response?.data || {};
                    if (seq !== geocodeSeqRef.current || !Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return;
                    const next = { ...latestRef.current, lat, lng };
                    latestRef.current = next;
                    setInputs(next);
                    setCurrentAddress(next);
                })
                .catch(() => {});
        }, ADDRESS_GEOCODE_DEBOUNCE_MS);
    };

    const addressIsValid = errMessage ? (!inputs?.street ? 'street' : !inputs?.city ? 'city' : !inputs?.country ? 'country' : '') : ' ';

    if (!googleReady) {
        return null;
    }

    return (
        <div>
            <div className={'flex-column'}>
                <PlacesAutocomplete
                    value={inputs?.street ? inputs?.street : ''}
                    onChange={(e) => handleChange(e, 'street')}
                    onSelect={(ev) => handleSelect(ev)}
                >
                    {({ getInputProps, suggestions, getSuggestionItemProps, loadSuggestions }) => {
                        return (
                            <div className={!oneInput ? globalStyles.addressSimpleInput : globalStyles.simpleInputFull}>
                                <TextField
                                    label={placeholder ? placeholder : `Physical Address${notRequired ? '' : '*'}`}
                                    value={inputs?.street ? inputs?.street : ''}
                                    variant="outlined"
                                    fullWidth
                                    className={
                                        noBlue
                                            ? classes.inputTextField
                                            : selectedAddress?.street
                                              ? globalStyles.inputTextFieldBlue
                                              : classes.inputTextField
                                    }
                                    {...getInputProps({
                                        disabled: disabled,
                                    })}
                                    error={addressIsValid === 'street'}
                                    InputProps={{
                                        endAdornment: loading && <InputMinLoader />,
                                    }}
                                />
                                {!noError && (
                                    <div style={{ position: 'relative' }}>
                                        <ErrMessage text={addressIsValid === 'street' ? 'Street is required' : ' '} />
                                    </div>
                                )}

                                {!noAddressSuggest && (
                                    <div style={{ position: 'relative' }}>
                                        {loadSuggestions && <div>Loading...</div>}

                                        <div className={classes.valuesContainer}>
                                            {suggestions.map((suggestion, index) => {
                                                const className = suggestion.active ? 'suggestion-item--active' : 'suggestion-item';
                                                const style = { margin: '15px', cursor: 'pointer' };
                                                return (
                                                    <div
                                                        key={index}
                                                        {...getSuggestionItemProps(suggestion, {
                                                            className,
                                                            style,
                                                        })}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <PlaceIcon style={{ color: Colors.ThemeBlue }} />
                                                            <span className={classes.searchfAddressDescriptionText}>
                                                                {suggestion.description}
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    }}
                </PlacesAutocomplete>

                {!noSuite && (
                    <ValidationInput
                        className={!noBlue && selectedAddress?.unitNumber && globalStyles.inputTextFieldBlue}
                        style={globalStyles.addressSimpleInput}
                        variant={'outlined'}
                        name={'unitNumber'}
                        type={'text'}
                        label={'Unit/Suit #'}
                        onChange={handleChange}
                        value={inputs?.unitNumber}
                        disabled={disabled}
                    />
                )}

                {!oneInput && (
                    <div className={classes.inputs}>
                        <ValidationInput
                            className={selectedAddress?.city && globalStyles.inputTextFieldBlue}
                            style={globalStyles.addressSimpleInput}
                            variant={'outlined'}
                            name={'city'}
                            type={'name'}
                            label={notRequired ? 'City' : 'City*'}
                            onChange={handleChange}
                            value={inputs?.city}
                            loader={loading}
                            disabled={disabled}
                            typeError={addressIsValid === 'city' ? 'City is required' : ''}
                        />
                        <ValidationInput
                            className={selectedAddress?.state && globalStyles.inputTextFieldBlue}
                            style={globalStyles.addressSimpleInput}
                            variant={'outlined'}
                            name={'state'}
                            type={'name'}
                            label={'State'}
                            onChange={handleChange}
                            value={inputs?.state}
                            loader={loading}
                            disabled={disabled}
                        />
                        <div className={'flex'} style={{ gap: 16 }}>
                            <SelectTypeAutocomplete
                                loadType={ADDRESS_LOAD}
                                style={'full-width'}
                                disabled={disabled}
                                title={notRequired ? 'Country' : 'Country*'}
                                name={'country'}
                                handleSelect={handleChange}
                                defaultValue={inputs?.country}
                                list={CountryList}
                                renderValue={(i) => i?.name}
                                error={addressIsValid}
                                typeError={addressIsValid === 'country' ? 'Country is required' : ''}
                                showErrorAlways={true}
                            />
                            {/*<SelectInput*/}
                            {/*    className={selectedAddress?.country && globalStyles.inputTextFieldBlue}*/}
                            {/*    style={globalStyles.addressSimpleInput}*/}
                            {/*    name={'country'}*/}
                            {/*    label={notRequired ? 'Country' : 'Country*'}*/}
                            {/*    handleSelect={handleChange}*/}
                            {/*    loader={loading}*/}
                            {/*    value={inputs?.country || 'US'}*/}
                            {/*    list={CountryList}*/}
                            {/*    disabled={disabled}*/}
                            {/*    typeError={addressIsValid === 'country' ? 'Country is required' : ''}*/}
                            {/*    showErrorAlways={true}*/}
                            {/*/>*/}

                            <ValidationInput
                                className={selectedAddress?.zip && globalStyles.inputTextFieldBlue}
                                style={globalStyles.simpleInputNoRight}
                                variant={'outlined'}
                                name={'zip'}
                                type={'text'}
                                label={'Zip Code'}
                                onChange={handleChange}
                                value={inputs?.zip}
                                loader={loading}
                                disabled={disabled}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
